/** @jsxImportSource @emotion/react */
import React, { useState, useCallback } from 'react'
import { css } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'
import {
  CheckboxOutlineIcon,
  CheckboxCheckedIcon,
  ChevronDown,
  ChevronRight,
  MoreVertIcon,
} from './icons'

// ─── Data Interfaces ─────────────────────────────────────────────

export interface TeamRow {
  id: string
  name: string
  games: number
  home: number
  visitor: number
  avgStartTime: string
  lateStartTime: string
  exceptions: number
}

export type DivisionScheduleStatus = 'pending' | 'generated' | 'approved'

export interface DivisionRow {
  id: string
  name: string
  games: number
  gamesPerTeam: number
  avgStartTime: string
  players: number
  teams: number
  divisionScore: string
  status?: DivisionScheduleStatus
  teamRows: TeamRow[]
}

// ─── Inline SVG Icons ────────────────────────────────────────────

const EditIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.PRIMARY_300,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04a1.003 1.003 0 000-1.42l-2.34-2.34a1.003 1.003 0 00-1.42 0l-1.83 1.83 3.75 3.75 1.84-1.82z'
      fill={color}
    />
  </svg>
)

const CheckCircleOutlineIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = colors.WARNING_200,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M16.59 7.58L10 14.17l-3.59-3.58L5 12l5 5 8-8-1.41-1.42zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8z'
      fill={color}
    />
  </svg>
)

const CheckIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 16,
  color = colors.PRIMARY_300,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 24 24'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z'
      fill={color}
    />
  </svg>
)

const ShowChartIcon: React.FC<{ size?: number; color?: string }> = ({
  size = 24,
  color = colors.PRIMARY_300,
}) => (
  <svg
    width={size}
    height={size}
    viewBox='0 0 22 17'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M20 5C18.55 5 17.74 6.44 18.07 7.51L14.52 11.07C14.22 10.98 13.78 10.98 13.48 11.07L10.93 8.52C11.27 7.45 10.46 6 9 6C7.55 6 6.73 7.44 7.07 8.52L2.51 13.07C1.44 12.74 0 13.55 0 15C0 16.1 0.9 17 2 17C3.45 17 4.26 15.56 3.93 14.49L8.48 9.93C8.78 10.02 9.22 10.02 9.52 9.93L12.07 12.48C11.73 13.55 12.54 15 14 15C15.45 15 16.27 13.56 15.93 12.48L19.49 8.93C20.56 9.26 22 8.45 22 7C22 5.9 21.1 5 20 5Z'
      fill={color}
    />
    <path
      d='M14 6L14.94 3.93L17 3L14.94 2.07L14 0L13.08 2.07L11 3L13.08 3.93L14 6Z'
      fill={color}
    />
    <path d='M2.5 8L3 6L5 5.5L3 5L2.5 3L2 5L0 5.5L2 6L2.5 8Z' fill={color} />
  </svg>
)

// ─── Status Badge ────────────────────────────────────────────────

const ReviewStatusBadge: React.FC<{ status?: DivisionScheduleStatus }> = ({
  status,
}) => {
  let bgColor: string
  let textColor: string
  let label: string

  switch (status) {
    case 'approved':
      bgColor = `${colors.SUCCESS_300}20`
      textColor = colors.SUCCESS_300
      label = 'Approved'
      break
    case 'generated':
      bgColor = `${colors.INFO_300}20`
      textColor = colors.INFO_300
      label = 'Generated'
      break
    case 'pending':
    default:
      bgColor = `${colors.WARNING_300}20`
      textColor = colors.WARNING_300
      label = 'Pending'
      break
  }

  return (
    <span
      css={css`
        display: inline-block;
        padding: 2px 8px;
        border-radius: 12px;
        background: ${bgColor};
        color: ${textColor};
        font-family: 'Inter', sans-serif;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        white-space: nowrap;
      `}
    >
      {label}
    </span>
  )
}

const checkboxBtnStyles = css`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &:hover {
    opacity: 0.8;
  }
`

// ─── Styles ──────────────────────────────────────────────────────

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 2px;
  margin-top: 16px;
`

/* Division header row */
const divisionHeaderRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 5px 16px;
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px;
`

const divisionHeaderNameColStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 306px;
  min-width: 306px;
  flex-shrink: 0;
`

const divisionHeaderCellStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
`

const divisionHeaderFlexColStyles = css`
  flex: 1;
  min-width: 0;
  min-height: 1px;
`

/* Division data row */
const divisionDataRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px;
  background-color: ${colors.SECONDARY_400};
  border-radius: 8px;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #1e2733;
  }
`

const divisionNameColStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 306px;
  min-width: 306px;
  flex-shrink: 0;
`

const divisionCellTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.WHITE};
`

const divisionFlexColStyles = css`
  flex: 1;
  min-width: 0;
  min-height: 1px;
`

const divisionScoreStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.SUCCESS_200};
`

const divisionActionsStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 97px;
  flex-shrink: 0;
`

const approveButtonStyles = css`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  background-color: ${colors.PRIMARY_300};
  border: none;
  border-radius: 8px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    opacity: 0.9;
  }
`

const approveButtonTextStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.SECONDARY_300};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

/* Team header row */
const teamHeaderRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 4px 16px 4px 32px;
  background-color: ${colors.SECONDARY_500};
`

/* Team data row */
const teamDataRowStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 16px 8px 32px;
  background-color: ${colors.SECONDARY_500};
`

const teamHeaderCellStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 17.4px;
  color: ${colors.NEUTRAL_70};
  text-transform: uppercase;
`

const teamNameColStyles = css`
  width: 290px;
  min-width: 290px;
  flex-shrink: 0;
`

const teamFlexColStyles = css`
  flex: 1;
  min-width: 0;
  min-height: 1px;
`

const teamDataCellStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  line-height: 20.3px;
  color: ${colors.NEUTRAL_30};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const teamExceptionsColStyles = css`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 88px;
  flex-shrink: 0;
`

const teamExceptionsCountStyles = css`
  font-family: 'Inter', sans-serif;
  font-size: 16px;
  font-weight: 400;
  line-height: 23.2px;
  color: ${colors.PRIMARY_300};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const teamActionsStyles = css`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
`

const iconBtnStyles = css`
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    opacity: 0.8;
  }
`

// ─── Component ───────────────────────────────────────────────────

interface ScheduleReviewTableProps {
  divisions: DivisionRow[]
  loading?: boolean
  onApproveAll?: () => void
  onApproveByDivisions?: (divisionUids: string[]) => void
  onRevertByDivisions?: (divisionUids: string[]) => void
}

const ScheduleReviewTable: React.FC<ScheduleReviewTableProps> = ({
  divisions,
  loading = false,
  onApproveAll,
  onApproveByDivisions,
  onRevertByDivisions,
}) => {
  const [expandedDivisions, setExpandedDivisions] = useState<Set<string>>(
    new Set()
  )
  const [selectedDivisions, setSelectedDivisions] = useState<Set<string>>(
    new Set()
  )

  const toggleDivisionSelection = useCallback(
    (e: React.MouseEvent, divId: string) => {
      e.stopPropagation()
      setSelectedDivisions((prev) => {
        const next = new Set(prev)
        if (next.has(divId)) {
          next.delete(divId)
        } else {
          next.add(divId)
        }
        return next
      })
    },
    []
  )

  const toggleSelectAll = useCallback(() => {
    setSelectedDivisions((prev) => {
      if (prev.size === divisions.length) {
        return new Set()
      }
      return new Set(divisions.map((d) => d.id))
    })
  }, [divisions])

  const handleApproveSelected = useCallback(() => {
    if (selectedDivisions.size > 0 && onApproveByDivisions) {
      onApproveByDivisions(Array.from(selectedDivisions))
    } else {
      onApproveAll?.()
    }
  }, [selectedDivisions, onApproveByDivisions, onApproveAll])

  const selectedHasApproved = divisions.some(
    (d) => selectedDivisions.has(d.id) && d.status === 'approved'
  )

  const handleRevertSelected = useCallback(() => {
    if (selectedDivisions.size > 0 && onRevertByDivisions) {
      const approvedSelected = divisions
        .filter((d) => selectedDivisions.has(d.id) && d.status === 'approved')
        .map((d) => d.id)
      if (approvedSelected.length > 0) {
        onRevertByDivisions(approvedSelected)
      }
    }
  }, [selectedDivisions, onRevertByDivisions, divisions])

  const allSelected =
    divisions.length > 0 && selectedDivisions.size === divisions.length

  const toggleDivision = useCallback((id: string) => {
    setExpandedDivisions((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }, [])

  return (
    <div css={containerStyles}>
      {/* Division column headers */}
      <div css={divisionHeaderRowStyles}>
        <div css={divisionHeaderNameColStyles}>
          {/* Select All checkbox */}
          <button
            type='button'
            css={checkboxBtnStyles}
            onClick={toggleSelectAll}
            aria-label='Select all divisions'
          >
            {allSelected ? (
              <CheckboxCheckedIcon size={20} />
            ) : (
              <CheckboxOutlineIcon size={20} />
            )}
          </button>
          {/* Chevron placeholder for alignment */}
          <div
            css={css`
              width: 24px;
              height: 8px;
              flex-shrink: 0;
              opacity: 0;
            `}
          />
          <div css={[divisionHeaderFlexColStyles]}>
            <span css={divisionHeaderCellStyles}>DIV NAME</span>
          </div>
        </div>
        <div css={divisionHeaderFlexColStyles}>
          <span css={divisionHeaderCellStyles}>GAMES</span>
        </div>
        <div css={divisionHeaderFlexColStyles}>
          <span css={divisionHeaderCellStyles}>TEAMS</span>
        </div>
        <div css={divisionHeaderFlexColStyles}>
          <span css={divisionHeaderCellStyles}>DIVISION SCORE</span>
        </div>
        <div
          css={css`
            flex-shrink: 0;
            display: flex;
            gap: 8px;
          `}
        >
          {selectedDivisions.size > 0 && (
            <button
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                background-color: transparent;
                border: 1px solid ${colors.NEUTRAL_50};
                border-radius: 8px;
                cursor: pointer;
                flex-shrink: 0;

                &:hover {
                  background-color: ${colors.SECONDARY_200};
                }

                &:disabled {
                  opacity: 0.4;
                  cursor: not-allowed;
                }
              `}
              type='button'
              disabled={loading}
              onClick={(e) => {
                e.stopPropagation()
                setSelectedDivisions(new Set())
              }}
            >
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 20.3px;
                  color: ${colors.NEUTRAL_50};
                `}
              >
                Cancel
              </span>
            </button>
          )}
          {onRevertByDivisions && selectedHasApproved && (
            <button
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
                padding: 4px 8px;
                background-color: ${colors.WARNING_300};
                border: none;
                border-radius: 8px;
                cursor: pointer;
                flex-shrink: 0;

                &:hover {
                  opacity: 0.9;
                }

                &:disabled {
                  opacity: 0.6;
                  cursor: not-allowed;
                }
              `}
              type='button'
              onClick={(e) => {
                e.stopPropagation()
                handleRevertSelected()
              }}
              disabled={loading}
            >
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-size: 14px;
                  font-weight: 500;
                  line-height: 20.3px;
                  color: ${colors.WHITE};
                `}
              >
                {loading
                  ? 'Reverting...'
                  : `Revert (${selectedDivisions.size})`}
              </span>
            </button>
          )}
          <button
            css={css`
              ${approveButtonStyles};
              &:disabled {
                opacity: 0.6;
                cursor: not-allowed;
              }
            `}
            type='button'
            onClick={(e) => {
              e.stopPropagation()
              handleApproveSelected()
            }}
            disabled={loading || divisions.length === 0}
          >
            <span css={approveButtonTextStyles}>
              {loading
                ? 'Approving...'
                : selectedDivisions.size > 0
                ? `Approve (${selectedDivisions.size})`
                : 'Approve All'}
            </span>
            {!loading && <CheckIcon size={16} color={colors.SECONDARY_300} />}
          </button>
        </div>
      </div>

      {/* Empty / loading state */}
      {divisions.length === 0 && !loading && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 32px 16px;
            background-color: ${colors.SECONDARY_400};
            border-radius: 8px;
            font-family: 'Inter', sans-serif;
            font-size: 14px;
            color: ${colors.NEUTRAL_50};
          `}
        >
          No schedule data yet. Generate a schedule to see division results
          here.
        </div>
      )}

      {/* Division data rows */}
      {divisions.map((div) => {
        const divExpanded = expandedDivisions.has(div.id)

        return (
          <React.Fragment key={div.id}>
            <div
              css={divisionDataRowStyles}
              onClick={() => toggleDivision(div.id)}
              role='button'
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  toggleDivision(div.id)
                }
              }}
            >
              <div css={divisionNameColStyles}>
                <button
                  type='button'
                  css={checkboxBtnStyles}
                  onClick={(e) => toggleDivisionSelection(e, div.id)}
                  aria-label={`Select ${div.name}`}
                >
                  {selectedDivisions.has(div.id) ? (
                    <CheckboxCheckedIcon size={20} />
                  ) : (
                    <CheckboxOutlineIcon size={20} />
                  )}
                </button>
                <div
                  css={css`
                    width: 24px;
                    height: 24px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                  `}
                >
                  {divExpanded ? (
                    <ChevronDown size={24} />
                  ) : (
                    <ChevronRight size={24} />
                  )}
                </div>
                <span css={divisionCellTextStyles}>{div.name}</span>
                {div.status && <ReviewStatusBadge status={div.status} />}
              </div>
              <div css={divisionFlexColStyles}>
                <span css={divisionCellTextStyles}>{div.games}</span>
              </div>
              <div css={divisionFlexColStyles}>
                <span css={divisionCellTextStyles}>{div.teams} Teams</span>
              </div>
              <div css={divisionFlexColStyles}>
                <span css={divisionScoreStyles}>{div.divisionScore}</span>
              </div>
              <div
                css={divisionActionsStyles}
                onClick={(e) => e.stopPropagation()}
              >
                <button css={iconBtnStyles} type='button' aria-label='Approve'>
                  <CheckIcon size={24} />
                </button>
                <button css={iconBtnStyles} type='button' aria-label='Stats'>
                  <ShowChartIcon size={24} />
                </button>
                <button
                  css={iconBtnStyles}
                  type='button'
                  aria-label='More options'
                >
                  <MoreVertIcon size={24} color={colors.PRIMARY_300} />
                </button>
              </div>
            </div>

            {/* Team header + data rows */}
            {divExpanded && div.teamRows.length > 0 && (
              <>
                {/* Team column headers */}
                <div css={teamHeaderRowStyles}>
                  <div css={[teamHeaderCellStyles, teamNameColStyles]}>
                    TEAM
                  </div>
                  <div css={[teamHeaderCellStyles, teamFlexColStyles]}>
                    GAMES
                  </div>
                  <div css={[teamHeaderCellStyles, teamFlexColStyles]}>
                    HOME
                  </div>
                  <div css={[teamHeaderCellStyles, teamFlexColStyles]}>
                    VISITOR
                  </div>
                  <div
                    css={css`
                      ${teamHeaderCellStyles};
                      width: 88px;
                      flex-shrink: 0;
                      text-align: center;
                    `}
                  >
                    EXCEPTIONS
                  </div>
                  {/* Actions placeholder for alignment */}
                  <div
                    css={css`
                      ${teamActionsStyles};
                      opacity: 0;
                      pointer-events: none;
                    `}
                  >
                    <EditIcon size={24} />
                    <MoreVertIcon size={24} />
                  </div>
                </div>

                {/* Team data rows */}
                {div.teamRows.map((team) => (
                  <div key={team.id} css={teamDataRowStyles}>
                    <div css={[teamDataCellStyles, teamNameColStyles]}>
                      {team.name}
                    </div>
                    <div css={[teamDataCellStyles, teamFlexColStyles]}>
                      {team.games}
                    </div>
                    <div css={[teamDataCellStyles, teamFlexColStyles]}>
                      {team.home}
                    </div>
                    <div css={[teamDataCellStyles, teamFlexColStyles]}>
                      {team.visitor}
                    </div>
                    <div css={teamExceptionsColStyles}>
                      <CheckCircleOutlineIcon
                        size={24}
                        color={colors.WARNING_200}
                      />
                      <span css={teamExceptionsCountStyles}>
                        {team.exceptions}
                      </span>
                    </div>
                    <div css={teamActionsStyles}>
                      <button
                        css={iconBtnStyles}
                        type='button'
                        aria-label='Edit'
                      >
                        <EditIcon size={24} />
                      </button>
                      <button
                        css={iconBtnStyles}
                        type='button'
                        aria-label='More options'
                      >
                        <MoreVertIcon size={24} color={colors.PRIMARY_300} />
                      </button>
                    </div>
                  </div>
                ))}
              </>
            )}
          </React.Fragment>
        )
      })}
    </div>
  )
}

export default ScheduleReviewTable
